import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { usePageTitle } from "@/hooks/usePageTitle";
import { MatchCard, Match } from "@/components/MatchCard";
import { Input } from "@/components/ui/input";
import { Search, Filter } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { usePersistedViewMode } from "@/utils/viewMode";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { useTranslation } from "react-i18next";
import { useMultiSelect } from "@/hooks/useMultiSelect";
import { EntityGrid } from "@/components/common/EntityGrid";
import { EntityList } from "@/components/common/EntityList";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Slider } from "@/components/ui/slider";
import { DuplicateManager, useDuplicateManager } from "@/components/common";
import { matchMergeConfig } from "@/config/matchMergeConfig";
import { EntityMergeService } from "@/services/entityMergeService";
import { MatchesViewOptions, SortField, SortOrder } from "@/components/matches/MatchesViewOptions";
import { useKanbanColumns } from "@/hooks/useKanbanColumns";

const Matches = () => {
	usePageTitle("Matches");
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	
	// Initialize state from URL parameters
	const [matches, setMatches] = useState<Match[]>([]);
	const [searchTerm, setSearchTerm] = useState(() => searchParams.get("search") || "");
	const [isLoading, setIsLoading] = useState(true);
	const [viewMode, setViewMode] = usePersistedViewMode('matches', 'card');
	const [showDeleteDialog, setShowDeleteDialog] = useState(false);
	const { t } = useTranslation();
	const { getStatusLabel } = useKanbanColumns();
	const [scoreFilter, setScoreFilter] = useState(() => {
		const score = searchParams.get("score");
		return score ? parseInt(score, 10) : 70;
	});
	const [scorePopoverOpen, setScorePopoverOpen] = useState(false);
	const [sortField, setSortField] = useState<SortField>(() => {
		const field = searchParams.get("sortField");
		return (field as SortField) || "createdAt";
	});
	const [sortOrder, setSortOrder] = useState<SortOrder>(() => {
		const order = searchParams.get("sortOrder");
		return (order as SortOrder) || "desc";
	});

	// Update URL parameters whenever filters change
	useEffect(() => {
		const params = new URLSearchParams(searchParams);
		
		// Clear only filter-related params
		params.delete("search");
		params.delete("score");
		params.delete("sortField");
		params.delete("sortOrder");
		
		if (searchTerm)
			params.set("search", searchTerm);
		if (scoreFilter !== 70)
			params.set("score", scoreFilter.toString());
		if (sortField !== "createdAt")
			params.set("sortField", sortField);
		if (sortOrder !== "desc")
			params.set("sortOrder", sortOrder);
		
		setSearchParams(params, { replace: true });
	}, [searchTerm, scoreFilter, sortField, sortOrder, searchParams]);

	useEffect(() => {
		fetchMatches();
	}, []);

	async function fetchMatches() {
		try {
			setIsLoading(true);
			
			const { data: matchesData, error: matchesError } = await supabase
				.from('matches')
				.select(`
					id,
					score,
					description,
					strengths,
					status,
					created_at,
					candidate_id,
					job_id,
					candidates (
						id,
						name
					),
					jobs (
						id,
						title,
						company_id,
						companies (
							id,
							name
						)
					)
				`);
			
			if (matchesError) throw matchesError;

			if (matchesData) {
				const formattedMatches: Match[] = (matchesData as any[]).map(match => ({
					id: match.id,
					candidate_id: match.candidate_id,
					job_id: match.job_id,
					score: match.score,
					description: match.description || "",
					strengths: match.strengths || [],
					weaknesses: null,
					status: match.status,
					ai_analysis: null,
					created_at: match.created_at,
					updated_at: null,
					candidateName: Array.isArray(match.candidates) && match.candidates[0]?.name ? match.candidates[0].name : match.candidates?.name || "Unknown Candidate",
					candidateId: match.candidate_id || "",
					jobId: match.job_id || "",
					jobTitle: Array.isArray(match.jobs) && match.jobs[0]?.title ? match.jobs[0].title : match.jobs?.title || "Unknown Position",
					companyId: Array.isArray(match.jobs) && Array.isArray(match.jobs[0]?.companies) && match.jobs[0].companies[0]?.id ? match.jobs[0].companies[0].id : match.jobs?.companies?.id || "",
					companyName: Array.isArray(match.jobs) && Array.isArray(match.jobs[0]?.companies) && match.jobs[0].companies[0]?.name ? match.jobs[0].companies[0].name : match.jobs?.companies?.name || "Unknown Company",
					createdAt: match.created_at
				}));
				
				setMatches(formattedMatches);
			}
		} catch (error) {
			console.error("Error fetching matches:", error);
			toast({
				title: "Failed to load matches",
				description: "An error occurred while loading matches.",
				variant: "destructive",
			});
		} finally {
			setIsLoading(false);
		}
	}

	const sortedMatches = matches
		.filter(match =>
			(match.score ?? 0) >= scoreFilter &&
			(
				(match.candidateName || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
				(match.jobTitle || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
				(match.companyName || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
				(match.description || "").toLowerCase().includes(searchTerm.toLowerCase())
			)
		)
		.sort((a, b) => {
			if (sortField === 'createdAt') {
				const dateA = new Date(a.createdAt || 0).getTime();
				const dateB = new Date(b.createdAt || 0).getTime();
				return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
			}
			if (sortField === 'score') {
				return sortOrder === 'asc' ? (a.score || 0) - (b.score || 0) : (b.score || 0) - (a.score || 0);
			}
			return 0;
		});

	const {
		isMultiSelectMode,
		toggleMultiSelect,
		toggleItemSelection,
		handleDeleteSelected,
		isSelected,
		selectedCount,
		selectAll
	} = useMultiSelect(sortedMatches, async (ids) => {
		// Implement your delete logic for matches here
	});
	const {
		openMergeModal,
		getDuplicateManagerProps
	} = useDuplicateManager(sortedMatches, isSelected, "matches", matchMergeConfig);
	// Handle match merge
	const handleMerge = async (merged: Match, sources: Match[]) => {
		try {
			const result = await EntityMergeService.mergeMatches(merged, sources);
			if (result.success) {
				toast({
					title: "Matches merged successfully",
					description: `${sources.length} matches have been merged into one.`,
				});
				// Refresh matches list
				fetchMatches();
				if (result.redirectPath)
					navigate(result.redirectPath);
				return result;
			} else {
				toast({
					title: "Failed to merge matches",
					description: result.error || "An error occurred during merge.",
					variant: "destructive",
				});
				// Rethrow error to prevent modal from closing
				throw new Error(result.error || "An error occurred during merge.");
			}
		} catch (error) {
			console.error("Merge error:", error);
			toast({
				title: "Failed to merge matches",
				description: "An unexpected error occurred.",
				variant: "destructive",
			});
			// Rethrow error to prevent modal from closing
			throw error;
		}
	};

	const columns = [
		{
			key: "candidateName",
			header: t("matches.candidate", "Candidate"),
			cell: (match: Match) => match.candidateName,
		},
		{
			key: "jobTitle",
			header: t("matches.jobTitle", "Job Title"),
			cell: (match: Match) => match.jobTitle,
		},
		{
			key: "companyName",
			header: t("matches.company", "Company"),
			cell: (match: Match) => match.companyName,
		},
		{
			key: "score",
			header: t("matches.score", "Score"),
			cell: (match: Match) => match.score,
		},
		{
			key: "status",
			header: t("matches.status", "Status"),
			cell: (match: Match) => match.status ? getStatusLabel(match.status) : t('matching.statusLabels.matched'),
		},
	];

	return (
		<div>
			<PageHeader
				title="Matches"
				description="Here are your champions!"
			/>
			
			<div className="mb-6 flex flex-col sm:flex-row gap-4 items-center">
				<div className="relative flex-1 w-full">
					<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
					<Input 
						type="text"
						placeholder="Search matches..."
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						className="pl-10"
					/>
				</div>
				
				<div className="flex items-center gap-2">
					<Popover open={scorePopoverOpen} onOpenChange={setScorePopoverOpen}>
						<PopoverTrigger asChild>
							<Button variant="outline" className="flex items-center gap-2" data-tour="matches-score-filter">
								<Filter className="h-4 w-4" />
								<span>Score</span>
								<span className="ml-2 text-xs text-gray-500 dark:text-gray-400">{scoreFilter}%+</span>
							</Button>
						</PopoverTrigger>
						<PopoverContent align="end" className="w-80">
							<div className="flex flex-col gap-4">
								<div className="flex items-center justify-between">
									<span className="text-sm font-medium">Minimal score</span>
									<span className="text-sm font-bold">{scoreFilter}%</span>
								</div>
								<Slider
									min={0}
									max={100}
									step={1}
									value={[scoreFilter]}
									onValueChange={([val]) => setScoreFilter(val)}
								/>
							</div>
						</PopoverContent>
					</Popover>
					<MatchesViewOptions
						sortField={sortField}
						setSortField={setSortField}
						sortOrder={sortOrder}
						setSortOrder={setSortOrder}
						viewMode={viewMode}
						setViewMode={setViewMode}
					/>
				</div>
			</div>
			
			{isLoading ? (
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
					{[1, 2, 3, 4, 5, 6].map(i => (
						<div key={i} className="bg-white dark:bg-gray-800 rounded-sm shadow p-5 animate-pulse">
							<div className="flex justify-between items-center mb-3">
								<div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
								<div className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded-sm"></div>
							</div>
							<div className="h-4 bg-gray-200 dark:bg-gray-700 rounded mb-4"></div>
							<div className="space-y-2">
								<div className="h-3 bg-gray-200 dark:bg-gray-700 rounded"></div>
								<div className="h-3 bg-gray-200 dark:bg-gray-700 rounded"></div>
								<div className="h-3 bg-gray-200 dark:bg-gray-700 rounded"></div>
							</div>
						</div>
					))}
				</div>
			) : (
				viewMode === 'card' ? (
					<EntityGrid
						data={sortedMatches}
						isLoading={isLoading}
						emptyMessage={"No matches found matching your search criteria."}
						renderItem={(match) => <MatchCard key={match.id} match={match} isSelected={isSelected(match.id)} />}
						isMultiSelectMode={isMultiSelectMode}
						onToggleMultiSelect={toggleMultiSelect}
						onToggleItemSelection={toggleItemSelection}
						isItemSelected={isSelected}
						onDeleteSelected={handleDeleteSelected}
						onMergeSelected={openMergeModal}
						selectedCount={selectedCount}
						selectAll={selectAll}
					/>
				) : (
					<EntityList
						data={sortedMatches}
						columns={columns}
						isLoading={isLoading}
						emptyMessage={"No matches found matching your search criteria."}
						basePath="/matches"
						isMultiSelectMode={isMultiSelectMode}
						onToggleMultiSelect={toggleMultiSelect}
						onToggleItemSelection={toggleItemSelection}
						isItemSelected={isSelected}
						onDeleteSelected={handleDeleteSelected}
						onMergeSelected={openMergeModal}
						selectedCount={selectedCount}
						selectAll={selectAll}
					/>
				)
			)}
			
			<AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t("common.deleteConfirmation", { count: selectedCount })}</AlertDialogTitle>
						<AlertDialogDescription>
							{t("common.deleteWarning", { count: selectedCount })}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => {
								handleDeleteSelected();
								setShowDeleteDialog(false);
							}}
							className="bg-red-600 hover:bg-red-700"
						>
							{t("common.delete")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
					<DuplicateManager
				{...getDuplicateManagerProps(handleMerge)}
			/>
		</div>
	);
};

export default Matches; 